import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, db } from '../lib/firebase';
import { ref, get } from 'firebase/database';

interface ThemeContextType {
  primaryColor: string;
  setPrimaryColor: (color: string) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [primaryColor, setPrimaryColor] = useState('#6366f1'); // Default indigo color

  // Load color directly from Firebase
  useEffect(() => {
    const loadQRColor = async () => {
      if (!auth.currentUser) return;

      try {
        const profileRef = ref(db, `vcards/${auth.currentUser.uid}`);
        const snapshot = await get(profileRef);
        
        if (snapshot.exists()) {
          const data = snapshot.val();
          if (data.qrStyle?.fgColor) {
            setPrimaryColor(data.qrStyle.fgColor);
          }
        }
      } catch (error) {
        console.error('Error loading QR color:', error);
      }
    };

    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        loadQRColor();
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    // Update CSS variables when primary color changes
    const root = document.documentElement;
    root.style.setProperty('--color-primary', primaryColor);
    
    // Generate different shades of the primary color
    const adjustBrightness = (hex: string, factor: number) => {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      
      const adjustedR = Math.min(255, Math.max(0, Math.round(r * factor)));
      const adjustedG = Math.min(255, Math.max(0, Math.round(g * factor)));
      const adjustedB = Math.min(255, Math.max(0, Math.round(b * factor)));
      
      return `#${adjustedR.toString(16).padStart(2, '0')}${adjustedG.toString(16).padStart(2, '0')}${adjustedB.toString(16).padStart(2, '0')}`;
    };

    // Set different shades
    root.style.setProperty('--color-primary-50', adjustBrightness(primaryColor, 1.5));
    root.style.setProperty('--color-primary-100', adjustBrightness(primaryColor, 1.4));
    root.style.setProperty('--color-primary-200', adjustBrightness(primaryColor, 1.3));
    root.style.setProperty('--color-primary-300', adjustBrightness(primaryColor, 1.2));
    root.style.setProperty('--color-primary-400', adjustBrightness(primaryColor, 1.1));
    root.style.setProperty('--color-primary-500', primaryColor);
    root.style.setProperty('--color-primary-600', adjustBrightness(primaryColor, 0.9));
    root.style.setProperty('--color-primary-700', adjustBrightness(primaryColor, 0.8));
    root.style.setProperty('--color-primary-800', adjustBrightness(primaryColor, 0.7));
    root.style.setProperty('--color-primary-900', adjustBrightness(primaryColor, 0.6));
  }, [primaryColor]);

  return (
    <ThemeContext.Provider value={{ primaryColor, setPrimaryColor }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
